import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronDown, ChevronUp, X } from 'lucide-react';
import { DanishCalendarMonth } from '@/components/ui/DanishCalendarMonth';
import { formatClinicCivilWeekdayDate, todayCivil } from '@/lib/questionnaireDateFormat';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

function shiftView(year: number, monthIndex: number, delta: number): { year: number; monthIndex: number } {
  const next = new Date(Date.UTC(year, monthIndex + delta, 1));
  return { year: next.getUTCFullYear(), monthIndex: next.getUTCMonth() };
}

export function DanishCalendarField({
  value,
  onChange,
  label,
  optional,
  markedDates,
  markedHint,
  min,
}: {
  value: string;
  onChange: (civil: string) => void;
  label?: string;
  optional?: string;
  markedDates?: readonly string[];
  markedHint?: string;
  min?: string;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const initial = value || todayCivil();
  const [year, setYear] = useState(() => Number(initial.slice(0, 4)));
  const [monthIndex, setMonthIndex] = useState(() => Number(initial.slice(5, 7)) - 1);
  const marked = useMemo(() => new Set(markedDates ?? []), [markedDates]);

  function shiftMonth(delta: number) {
    const next = shiftView(year, monthIndex, delta);
    setYear(next.year);
    setMonthIndex(next.monthIndex);
  }

  return (
    <div className="fld sk">
      {label ? (
        <span className="lbl">
          {label}
          {optional ? <span className="o"> {optional}</span> : null}
        </span>
      ) : null}
      <button
        type="button"
        className={cn('dsel', open && 'focus')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <CalendarDays className="i sm" aria-hidden />
        <span className="v num">
          {value
            ? formatClinicCivilWeekdayDate(value, i18n.language)
            : t('forms.picker.choose_date', 'Vælg dato')}
        </span>
        {open ? <ChevronUp className="i sm" aria-hidden /> : <ChevronDown className="i sm" aria-hidden />}
      </button>
      {open ? (
        <div className="calp" role="dialog" aria-label={t('forms.picker.calendar_label', 'Vælg frist')}>
          <DanishCalendarMonth
            year={year}
            monthIndex={monthIndex}
            language={i18n.language}
            value={value}
            marked={marked}
            min={min}
            onShift={shiftMonth}
            onSelect={(civil) => {
              onChange(civil);
              setOpen(false);
            }}
          />
          {markedHint ? (
            <p className="cf">
              <i />
              {markedHint}
            </p>
          ) : null}
          {value ? (
            <button
              type="button"
              className={cn(TYPE.small, 'mt-2 inline-flex items-center gap-1 font-semibold text-ds-teal')}
              onClick={() => onChange('')}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              {t('common.clear', 'Ryd')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
